import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../setupAxios";
import { useAuth } from "../context/authContext";
import { onCambioResumen, onActualizarPanel, avisarActualizarPanel, onCambioAlertas } from "../statsBus";
import { TIPOS_ALERTA } from "./Alertas";

// Cada cuánto se miran las alertas de llaves (Administración > 🔔 Alertas).
const INTERVALO_ALERTAS_MS = 2 * 60 * 1000;
// Rutas: solo se mira si ha cambiado algo (consulta mínima), no la lista.
const INTERVALO_RUTAS_MS = 60 * 1000;
const claveNotificadas = (u) => `alertas_notificadas_${u || ""}`;
const claveRutasNotificadas = (u) => `rutas_notificadas_${u || ""}`;

// Versión de la app que tiene cargada esta pestaña (el main.<hash>.js del
// build). Si en el servidor hay otra, se avisa para recargar: así nunca se
// queda nadie trabajando con una versión vieja sin saberlo.
const INTERVALO_VERSION_MS = 2 * 60 * 1000;
const scriptActual = () => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  return el ? new URL(el.getAttribute("src"), window.location.origin).pathname : null;
};

// Devuelve los ids de `ids` que aún no se habían avisado en este navegador y
// los apunta como avisados (cada cosa se avisa una sola vez).
function nuevosSinAvisar(clave, ids) {
  let ya = [];
  try { ya = JSON.parse(localStorage.getItem(clave) || "[]"); } catch { ya = []; }
  const yaSet = new Set(ya);
  const nuevos = ids.filter((id) => !yaSet.has(id));
  if (nuevos.length) {
    try { localStorage.setItem(clave, JSON.stringify([...ya, ...nuevos].slice(-500))); } catch { /* sin almacenamiento */ }
  }
  return nuevos;
}

// Barra superior: marca, estadísticas, navegación por rol y usuario/salir.
// Las estadísticas (pisos / prestadas ahora) las carga la propia barra, así
// que aparecen siempre, en cualquier página (Gestionar, Rutas o Administración).
export default function TopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [confirmSalir, setConfirmSalir] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [stats, setStats] = useState(null);
  const [pendRutas, setPendRutas] = useState(0);
  const [misSolicitudes, setMisSolicitudes] = useState(0);
  const [alertas, setAlertas] = useState(null);   // /alertas/resumen
  const [avisoAlertas, setAvisoAlertas] = useState(null); // {n, por_tipo} alertas nuevas aún no avisadas
  const [avisoRutas, setAvisoRutas] = useState(null);     // {total, nuevas:[ruta]} rutas nuevas aún no avisadas
  const [versionNueva, setVersionNueva] = useState(false);

  useEffect(() => {
    const actual = scriptActual();
    if (!actual) return undefined;   // en desarrollo (npm start) no hay build
    const comprobar = () => {
      if (document.visibilityState !== "visible") return;
      fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((m) => {
          const main = (m?.entrypoints || []).find((f) => /static\/js\/main\./.test(f));
          if (main && `/${main.replace(/^\//, "")}` !== actual) setVersionNueva(true);
        }).catch(() => {});
    };
    comprobar();
    const t = setInterval(comprobar, INTERVALO_VERSION_MS);
    document.addEventListener("visibilitychange", comprobar);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", comprobar); };
  }, []);

  // Alertas de llaves: contador en "Administración" y aviso emergente cuando
  // aparece alguna nueva (cada alerta se avisa una sola vez por navegador).
  useEffect(() => {
    if (user?.tipo !== "llaves") return undefined;
    const cargar = () => {
      if (document.visibilityState !== "visible") return;   // pestaña oculta: no pregunta
      api.get("/alertas/resumen").then(({ data }) => {
        setAlertas(data);
        window.dispatchEvent(new CustomEvent("alertas:resumen", { detail: data }));
        const nuevas = nuevosSinAvisar(claveNotificadas(user?.user), data.ids_nuevas || []);
        if (nuevas.length) setAvisoAlertas({ n: nuevas.length, total: data.nuevas, por_tipo: data.por_tipo || {} });
      }).catch(() => {});
    };
    cargar();
    const t = setInterval(cargar, INTERVALO_ALERTAS_MS);
    const q1 = onCambioAlertas(cargar);
    const q2 = onActualizarPanel(cargar);
    const q3 = onCambioResumen(cargar);
    return () => { clearInterval(t); q1(); q2(); q3(); };
  }, [user?.tipo, user?.user]);

  const verRutas = () => { setAvisoRutas(null); nav("/rutas"); };
  const verAlertas = () => {
    setAvisoAlertas(null);
    nav("/admin#alertas");
    // Si ya se estaba en Administración, que cambie de panel.
    setTimeout(() => window.dispatchEvent(new HashChangeEvent("hashchange")), 0);
  };

  useEffect(() => {
    if (user?.tipo !== "llaves") return undefined;
    // Firma de cada ruta pendiente: id + última creación/modificación. Una
    // firma que no se había visto = ruta nueva o modificada desde la última vez.
    const clave = claveRutasNotificadas(user?.user);
    const firma = (r) => `${r.id}|${r.fecha_modificacion || r.fecha_pedido}`;
    let version = null;
    const cargarRutas = (avisar) => api.get("/rutas/pendientes").then(({ data }) => {
      setPendRutas(data.n_rutas || 0);
      const rows = data.rows || [];
      let vistas = null;
      try { vistas = JSON.parse(localStorage.getItem(clave) || "null"); } catch { vistas = null; }
      const primeraVez = !Array.isArray(vistas);
      const set = new Set(vistas || []);
      const ids = new Set((vistas || []).map((f) => String(f).split("|")[0]));
      const cambiadas = rows.filter((r) => !set.has(firma(r)));
      try { localStorage.setItem(clave, JSON.stringify([...set, ...cambiadas.map(firma)].slice(-500))); } catch { /* sin almacenamiento */ }
      // Sin aviso la primera vez (lo que ya había) ni por los cambios hechos por uno mismo.
      const paraAvisar = cambiadas.filter((r) => r.modificada_por !== user?.user);
      if (avisar && !primeraVez && paraAvisar.length) {
        setAvisoRutas({ total: data.n_rutas || 0,
                        nuevas: paraAvisar.map((r) => ({ ...r, modificada: ids.has(String(r.id)) })) });
      }
    }).catch(() => {});
    const cargar = () => {
      api.get("/resumen").then(({ data }) => setStats(data)).catch(() => {});
      cargarRutas(true);
    };
    // Comprobación mínima: solo si ha cambiado la "versión" de las rutas se
    // pide la lista. Y solo mientras la pestaña está a la vista.
    const comprobarRutas = () => {
      if (document.visibilityState !== "visible") return;
      api.get("/rutas/version").then(({ data }) => {
        if (version !== null && data.v !== version) cargarRutas(true);
        version = data.v;
      }).catch(() => {});
    };
    cargar();
    comprobarRutas();
    const t = setInterval(comprobarRutas, INTERVALO_RUTAS_MS);
    const alVolver = () => { if (document.visibilityState === "visible") comprobarRutas(); };
    document.addEventListener("visibilitychange", alVolver);
    const quitarCambio = onCambioResumen(cargar);
    const quitarActualizar = onActualizarPanel(cargar);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", alVolver); quitarCambio(); quitarActualizar(); };
  }, [user?.tipo, user?.user]);

  // Para el equipo (rol 'personal', incluido el tipo 'llaves'): cuántas
  // solicitudes suyas están ya "preparado" (listas para recoger) — es lo
  // realmente accionable para el solicitante, así que se destaca en rojo.
  useEffect(() => {
    if (user?.tipo === "llaves") return undefined;
    const cargar = () => {
      api.get("/rutas/mias").then(({ data }) =>
        setMisSolicitudes((data.rows || []).filter((r) => r.estado === "preparado").length)
      ).catch(() => {});
    };
    cargar();
    const quitarCambio = onCambioResumen(cargar);
    const quitarActualizar = onActualizarPanel(cargar);
    return () => { quitarCambio(); quitarActualizar(); };
  }, [user?.tipo]);

  // Al cambiar de página, si el menú móvil estaba abierto, ciérralo.
  useEffect(() => { setMenuAbierto(false); }, [loc.pathname]);

  const salir = () => {
    setConfirmSalir(false);
    logout();
    nav("/login", { replace: true });
  };

  const tab = (to, label, badge, alerta = false) => (
    <button
      key={to}
      className={[loc.pathname === to ? "active" : "", (badge > 0 && alerta) ? "alerta" : ""].filter(Boolean).join(" ")}
      onClick={() => nav(to)}
    >
      {label}
      {badge > 0 && <span className={`tab-badge ${alerta ? "" : "tab-badge-neutral"}`}>{badge}</span>}
    </button>
  );

  const nombre = user?.nombre || user?.user || "";
  const inicial = nombre.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={`topbar ${menuAbierto ? "topbar-open" : ""}`}>
      <div className="topbar-top">
        <div className="topbar-brand">
          <img src="/favicon.ico" alt="" className="topbar-logo" />
          Gestor de <span>Llaves</span> 🔑
        </div>
        <button
          type="button"
          className="topbar-toggle"
          aria-label={menuAbierto ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={menuAbierto}
          onClick={() => setMenuAbierto((v) => !v)}
        >
          {menuAbierto ? "✕" : "☰"}
        </button>
      </div>

      <div className="topbar-right">
        {stats && user?.tipo === "llaves" && (
          <div className="topbar-stats">
            <div className="stat-chip"><b>{stats.pisos}</b><span>Pisos</span></div>
            <div className="stat-chip"><b>{stats.prestadas}</b><span>Prestadas ahora</span></div>
          </div>
        )}

        <div className="tabs">
          {user?.tipo === "llaves" && tab("/operar", "🔑 Gestionar")}
          {tab("/rutas", "🚚 Rutas", user?.tipo === "llaves" ? pendRutas : misSolicitudes, true)}
          {user?.tipo === "llaves" && tab("/admin", "⚙️ Administración", alertas?.nuevas || 0, true)}
        </div>

        <button className="btn btn-refresh" onClick={() => avisarActualizarPanel()} title="Actualizar los datos de esta pantalla">
          🔄 Actualizar
        </button>

        <div className="topbar-user">
          <div className="user-chip">
            <span className="avatar">{inicial}</span>
            <span className="topbar-name">{nombre}</span>
          </div>
          <button className="btn-salir" onClick={() => setConfirmSalir(true)}>
            <span aria-hidden="true">🚪</span> Salir
          </button>
        </div>
      </div>

      {versionNueva && (
        <div className="aviso-version" role="alert">
          <span>🔄 <b>Hay una versión nueva de la app.</b> Recarga para ver los últimos cambios.</span>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Recargar ahora</button>
        </div>
      )}

      <div className="avisos-flotantes">
      {avisoRutas && (
        <div className="aviso-alertas aviso-rutas" role="alert">
          <div className="aa-cab">
            <span className="aa-ico">🚚</span>
            <b>{avisoRutas.nuevas.length === 1
              ? (avisoRutas.nuevas[0].modificada ? "1 ruta modificada" : "1 ruta nueva por preparar")
              : `${avisoRutas.nuevas.length} rutas nuevas o modificadas`}</b>
            <button type="button" className="aa-x" aria-label="Cerrar" onClick={() => setAvisoRutas(null)}>✕</button>
          </div>
          <ul>
            {avisoRutas.nuevas.slice(0, 5).map((r) => (
              <li key={r.id}>{r.modificada ? "✏️ Modificada" : "🆕 Nueva"} · Nº {r.id} · <b>{r.solicitante || "—"}</b> · {r.n_llaves || 0} llave(s)</li>
            ))}
            {avisoRutas.nuevas.length > 5 && <li>… y {avisoRutas.nuevas.length - 5} más</li>}
          </ul>
          {avisoRutas.total > avisoRutas.nuevas.length && (
            <p className="aa-sub">En total hay {avisoRutas.total} ruta(s) pendientes de preparar.</p>
          )}
          <button type="button" className="btn btn-primary aa-ver" onClick={verRutas}>Ver rutas</button>
        </div>
      )}
      {avisoAlertas && (
        <div className="aviso-alertas" role="alert">
          <div className="aa-cab">
            <span className="aa-ico">🔔</span>
            <b>{avisoAlertas.n === 1 ? "1 alerta nueva de llaves" : `${avisoAlertas.n} alertas nuevas de llaves`}</b>
            <button type="button" className="aa-x" aria-label="Cerrar" onClick={() => setAvisoAlertas(null)}>✕</button>
          </div>
          <ul>
            {Object.entries(avisoAlertas.por_tipo).filter(([, n]) => n > 0).map(([k, n]) => (
              <li key={k}>{TIPOS_ALERTA[k]?.ico} {TIPOS_ALERTA[k]?.label || k}: <b>{n}</b></li>
            ))}
          </ul>
          <button type="button" className="btn btn-primary aa-ver" onClick={verAlertas}>Ver alertas</button>
        </div>
      )}
      </div>

      {confirmSalir && (
        <div className="modal-back" onMouseDown={() => setConfirmSalir(false)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <h3>👋 ¿Salir de la aplicación?</h3>
            <p className="muted">Se cerrará la sesión de <b>{nombre}</b>.</p>
            <div className="modal-actions">
              <button className="btn" onClick={() => setConfirmSalir(false)}>Cancelar</button>
              <button className="btn btn-danger" onClick={salir}>Sí, salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
